var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useContext, useCallback, useMemo, useRef, } from "react";
import { Popper, Manager, Reference, } from "react-popper";
import { css } from "styled-components";
import ReactDOM from "react-dom";
import { styled } from "../styling";
import useClickAway from "react-use/lib/useClickAway";
import { useMeasure } from "@insync-stageplayer/hooks";
import { MdClose } from "react-icons/md";
import { useCombinedRefs } from "../_internal/util/ref";
import { ControlButton } from "../ControlButton";
import { PopoutTargetContext } from "./PopoutTargetContext";
import { Surface } from "../Surface";
import { useTheme } from "../styling";
const defaultArrowSize = 8;
const arrowSize = ({ arrowSize }) => arrowSize;
const paddingStyle = ({ placement = "top", arrowSize, }) => {
    const basePositioning = placement.split("-")[0];
    const padding = {
        top: basePositioning === "bottom" ? arrowSize : 0,
        right: basePositioning === "left" ? arrowSize : 0,
        bottom: basePositioning === "top" ? arrowSize : 0,
        left: basePositioning === "right" ? arrowSize : 0,
    };
    return css `
    padding: ${padding.top}px ${padding.right}px ${padding.bottom}px
      ${padding.left}px;
  `;
};
const arrowPart = (creator) => (props) => creator(props);
const topArrow = arrowPart(() => css `
    bottom: 0;
    clip-path: polygon(50% 100%, 0 0, 100% 0);
  `);
const bottomArrow = arrowPart(() => css `
    top: 0;
    clip-path: polygon(50% 0%, 0 100%, 100% 100%);
  `);
const horizontalStartArrow = arrowPart(() => css `
    margin-right: 5px;
  `);
const horizontalEndArrow = arrowPart(() => css `
    margin-left: 5px;
  `);
const verticalStartArrow = arrowPart(() => css `
    margin-bottom: 5px;
  `);
const verticalEndArrow = arrowPart(() => css `
    margin-top: 5px;
  `);
const rightArrow = arrowPart(() => css `
    left: 0;
    clip-path: polygon(100% 0%, 0 50%, 100% 100%);
  `);
const leftArrow = arrowPart(() => css `
    right: 0;
    clip-path: polygon(0% 0%, 100% 50%, 0% 100%);
  `);
const arrowStyles = (props) => ({
    top: topArrow(props),
    "top-start": css `
    ${topArrow(props)}
    ${horizontalStartArrow(props)}
  `,
    "top-end": css `
    ${topArrow(props)}
    ${horizontalEndArrow(props)}
  `,
    right: rightArrow(props),
    "right-start": css `
    ${rightArrow(props)}
    ${verticalStartArrow(props)}
  `,
    "right-end": css `
    ${rightArrow(props)}
    ${verticalEndArrow(props)}
  `,
    bottom: bottomArrow(props),
    "bottom-start": css `
    ${bottomArrow(props)}
    ${horizontalStartArrow(props)}
  `,
    "bottom-end": css `
    ${bottomArrow(props)}
    ${horizontalEndArrow(props)}
  `,
    left: leftArrow(props),
    "left-start": css `
    ${leftArrow(props)}
    ${verticalStartArrow(props)}
  `,
    "left-end": css `
    ${leftArrow(props)}
    ${verticalEndArrow(props)}
  `,
});
const PopoutContentWrapper = styled.div `
  ${paddingStyle}
`;
const PopoutContent = styled(Surface) `
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 5px;
  padding: 5px;
  overflow: hidden;
  z-index: 1234;
`;
const PopoutContentTootip = styled.div `
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  padding: 7px 10px 7px 10px;
  overflow: hidden;
  z-index: 1234;
  background-color: #282828 !important;
  color: white !important;
  font: normal normal normal 15px/17px "Open Sans", sans-serif;
  box-shadow: rgb(0 0 0 / 40%) 0px 0px 5px;
  letter-spacing: 0px;
`;
const Header = styled.div `
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
const CloseButton = styled(ControlButton) `
  box-sizing: border-box;
  height: 15px;
`;
const Arrow = styled.div `
  width: ${arrowSize}px;
  height: ${arrowSize}px;
  z-index: 3333;
  background-color: ${(props) => props.theme.name === "noldus" ? "#282828" : props.theme.colors.accent};
  margin-top: 0px;
  color: ${(props) => props.theme.colors.foreground};
  position: absolute;
  border-color: transparent;
  ${(props) => {
    if (props.placement) {
        const placement = props.placement;
        return arrowStyles(props)[placement];
    }
}}
`;
const PopoutContainer = React.forwardRef((props, ref) => {
    const { style, arrowProps, placement, arrowSize, children, scheduleUpdate, closeable, onClose, onClickOutside, } = props;
    const [measureRef, observable] = useMeasure();
    const clickAwayRef = useRef(null);
    useClickAway(clickAwayRef, () => {
        onClickOutside && onClickOutside();
    });
    useEffect(() => {
        const subscription = observable.subscribe(scheduleUpdate);
        return () => subscription.unsubscribe();
    }, [observable, scheduleUpdate]);
    const theme = useTheme();
    return (React.createElement("div", { ref: useCombinedRefs(ref, measureRef, clickAwayRef), style: style },
        React.createElement(PopoutContentWrapper, { arrowSize: arrowSize, placement: placement, "data-testid": "popoutcontent" },
            React.createElement(Arrow, Object.assign({}, arrowProps, { arrowSize: arrowSize, placement: placement })),
            theme.name === "noldus" ? (React.createElement(PopoutContentTootip, null,
                closeable && (React.createElement(Header, null,
                    React.createElement(CloseButton, { onClick: () => onClose && onClose(), "data-testid": "popout-closebutton" },
                        React.createElement(MdClose, { size: 15 })))),
                children)) : (React.createElement(PopoutContent, { elevation: 10 },
                closeable && (React.createElement(Header, null,
                    React.createElement(CloseButton, { onClick: () => onClose && onClose(), "data-testid": "popout-closebutton" },
                        React.createElement(MdClose, { size: 15 })))),
                children)))));
});
export const Popout = ({ placement: position = "top", children, visible = true, bounds, content, target: targetProp = React.createRef(), popoutRef, onClickOutside, arrowSize = defaultArrowSize, closeable = false, onClose, }) => {
    const contextTarget = useContext(PopoutTargetContext);
    const target = targetProp.current
        ? targetProp.current
        : contextTarget.current;
    const modifiers = useMemo(() => ({
        preventOverflow: {
            enabled: true,
            padding: 0,
            boundariesElement: (bounds === null || bounds === void 0 ? void 0 : bounds.current) || "viewport",
        },
    }), [bounds]);
    const childrenFn = useCallback(({ ref: popperRef }) => children({ ref: popperRef }), [children]);
    return (React.createElement(Manager, null,
        React.createElement(Reference, null, childrenFn),
        visible && (React.createElement(Popper, { placement: position, modifiers: modifiers, positionFixed: true, innerRef: popoutRef }, (_a) => {
            var { ref } = _a, popperProps = __rest(_a, ["ref"]);
            const toRender = (React.createElement(PopoutContainer, Object.assign({}, popperProps, { ref: ref, arrowSize: arrowSize, closeable: closeable, onClose: onClose, onClickOutside: onClickOutside }), content));
            return target ? ReactDOM.createPortal(toRender, target) : toRender;
        }))));
};
//# sourceMappingURL=Popout.js.map